import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, IconButton, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { useFormaliteService } from '@europrocurement/l2d-domain';
import { enqueueSnackbar } from 'notistack';
import { useSelectedDossierContext, useDossierActionContext } from '../../../providers';
import { ModalPaper } from '../ExportDossierModal/ModalPaper';
import SelectGreffeContent from './SelectGreffeContent';
import { GreffeChoice } from './GreffeCard';

const SelectGreffeModal: React.FunctionComponent = function () {
    const { currentAction, cancelCurrentAction, confirmCurrentAction, dossiers } =
        useDossierActionContext();
    const { palette } = useTheme();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [currentChoices, setCurrentChoices] = useState<Array<GreffeChoice>>([]);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const { refreshDossier } = useSelectedDossierContext();
    const { dossierApi } = useFormaliteService();

    useEffect(() => {
        const shouldShowModal = currentAction === 'selectGreffe';
        if (shouldShowModal !== showModal) {
            setSubmitting(false);
            setShowModal(shouldShowModal);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentAction]);

    useEffect(() => {
        const shouldShowModal = currentAction === 'selectGreffe';
        if (shouldShowModal !== showModal) {
            setCurrentChoices(
                dossiers
                    .filter((dossier) => dossier.greffeId !== null)
                    .map((dossier) => ({
                        id: dossier.greffeId as number,
                        greffes: dossier.greffeLibelle as string,
                    })),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossiers]);

    const handleModalClose = () => {
        cancelCurrentAction();
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        const promises = dossiers.map((dossier) =>
            dossierApi.apiDossiersIdPatch({
                id: dossier.id as unknown as string,
                dossierDossierUpdatePartial: { greffe: currentChoices[0]?.id },
            }),
        );

        try {
            await Promise.all(promises);
            enqueueSnackbar('Le dossier a bien été sauvegardé');
            confirmCurrentAction();
            refreshDossier();
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Une erreur est survenue lors de la sauvegarde du greffe', {
                variant: 'error',
            });
            setSubmitting(false);
        }
    };

    const cancelBtnSx = {
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '0 10px',
    };

    const submitBtnSx = {
        backgroundColor: 'primary.dark',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '0 10px',
    };

    return (
        <Dialog
            open={showModal}
            PaperComponent={ModalPaper}
        >
            <IconButton
                aria-label="close"
                onClick={handleModalClose}
                sx={{
                    fontSize: '16px',
                    position: 'absolute',
                    right: '20px',
                    top: '30px',
                    color: palette.grey[500],
                }}
            >
                <FaOptionIcon icon={['fas', 'close']} />
            </IconButton>
            <SelectGreffeContent
                onSelectGreffe={(greffes: Array<GreffeChoice>) => setCurrentChoices(greffes)}
                currentChoices={currentChoices}
            />
            <Box
                sx={{
                    marginTop: '1em',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '0 1em',
                }}
            >
                <Button
                    variant="outlined"
                    onClick={handleModalClose}
                    sx={cancelBtnSx}
                    disabled={submitting}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    sx={submitBtnSx}
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    Valider
                </Button>
            </Box>
        </Dialog>
    );
};

export default SelectGreffeModal;
